/**
 * Accounts. Username and password, server-side sessions, no email in v1
 * (Q8.3, Q0.4).
 *
 * The username is the only string a player writes anywhere in the game, and in
 * v1 no other player ever sees it: there is no leaderboard, letters are
 * anonymous and the forum is post-v1. It is constrained anyway, because the
 * forum will change that and names chosen now carry over (Q11.5).
 */

import { z } from 'zod'
import { GAME_CONFIG } from '../game-config.ts'
import { reputationTierSchema } from './common.ts'

const { username } = GAME_CONFIG.account

export const usernameSchema = z
  .string()
  .min(username.minLength)
  .max(username.maxLength)
  .regex(username.pattern)

/**
 * Length only. Complexity rules push people toward one bad password they reuse;
 * length is the constraint that helps, and the hash is Argon2id.
 */
export const passwordSchema = z.string().min(10).max(200)

export const registerRequestSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
})

export const loginRequestSchema = z.object({
  username: z.string(),
  password: z.string(),
})

/**
 * The player, to themselves. Reputation appears as a tier and nothing else:
 * never the score, never the counts behind it (Q5.2).
 */
export const meSchema = z.object({
  username: usernameSchema,
  seal: reputationTierSchema,
  /** The locale to render in. Defaults from `Accept-Language` (Q12.5). */
  locale: z.string(),
  /** Empty in v1 (Q10.4). */
  abilities: z.array(z.string()),
})
export type ApiMe = z.infer<typeof meSchema>

export const sessionResponseSchema = z.object({ user: meSchema })
export const meResponseSchema = z.object({ user: meSchema.nullable() })
